import { useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import useMedia from 'use-media';
import { Trans } from '@lingui/react';
import Link from 'next/link';
import Icon from '@components/Icon';
import { Box, Flex } from '@components/layout/Grid';
import {
  EssentialGenericFieldsFragment,
  ProgramCategory,
} from '@gql/generated';
import { hexToRgbA } from '@lib/utils';
import {
  ProgramCardInfo,
  Title,
  Description,
} from '@organisms/cards/ProgramListingCard/ProgramCardInfo';
import LanguageContext from '@lib/contexts/languageContext';
import ArrowRightIcon from '@assets/ArrowRightIcon';
import ProgramListingCard from '@organisms/cards/ProgramListingCard';
import { useFormatDuration } from '@lib/hooks/useFormatDuration';

const CardLink = styled(Link)`
  grid-column: 1 / span 3;
  display: flex;
  color: #242424;
  text-decoration: none;
`;

const Card = styled.div`
  display: flex;
  width: 100%;

  ${Title} {
    font-size: 24px;
    color: #242424;
  }

  ${Description} {
    flex: none;
    max-width: 600px;
    font-size: 16px;
    color: #242424;
  }
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
`;

const Thumbnail = styled(Box)`
  width: calc(50% - 40px);
  position: relative;
  display: flex;
  padding-top: 28%;
  margin-right: 40px;
  background-color: ${({ theme }) => theme.colors.secondary['gray-4']};
  overflow: hidden;
`;

const ProgramDuration = styled.p<{ appearance?: ProgramCategory }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 500;
  margin: 0;
  background-color: ${({ appearance, theme }) => {
    if (appearance === ProgramCategory['Challenges']) {
      return hexToRgbA(theme.colors.primary.green, 0.8);
    } else if (appearance === ProgramCategory['Courses']) {
      return hexToRgbA(theme.colors.secondary.gold, 0.8);
    } else {
      return 'rgba(102, 124, 145, 0.9)';
    }
  }};

  i,
  svg {
    color: inherit;
    margin-bottom: 8px;

    :before {
      font-size: 32px;
      color: inherit;
    }
  }
`;

const DiscoverLink = styled.span`
  display: flex;
  align-items: center;
  color: #129edc;
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;

  svg {
    margin-left: 10px;
    path {
      fill: #129edc;
    }
  }
`;

type Props = {
  className?: string;
  program: EssentialGenericFieldsFragment;
  programCategory: ProgramCategory;
  href?: string;
};
export const SingleProgramCard = (props: Props) => {
  const { className = '', program, programCategory, href } = props;
  const lang = useContext(LanguageContext);
  const formatDuration = useFormatDuration();
  const theme = useTheme();
  const mobile = useMedia(`(max-width: ${theme.viewports.tablet}px)`);

  if (mobile) {
    return (
      <ProgramListingCard
        program={program}
        programCategory={programCategory}
        href={href}
        accessStatus={null}
      />
    );
  }

  return (
    <CardLink
      href={href || `/${lang}/${programCategory}/${program.slug}/overview`}
      prefetch={false}
    >
      <Card className={className}>
        <Thumbnail data-testid="ProgramListingCard--Thumbnail">
          <Image src={program.titleImageUrl} loading="lazy" />
          <ProgramDuration
            appearance={programCategory}
            data-testid="ProgramListingCard--Duration"
          >
            <Icon name="ygb-icon-program-ic" />
            <span>
              {formatDuration({
                [program.duration.unit]: program.duration.value,
              })}
            </span>
          </ProgramDuration>
        </Thumbnail>
        <Flex flex={1} flexDirection="column" justifyContent="center" py={50}>
          <ProgramCardInfo
            title={program.title}
            desc={program.description}
            shortDesc={program.shortDescription}
            instructors={program.instructors}
            category={programCategory}
          />
          <DiscoverLink>
            {programCategory === ProgramCategory['Challenges'] ? (
              <Trans id="bundle.id_page.challenge.discover_button" />
            ) : (
              <Trans id="bundle.id_page.program.discover_button" />
            )}
            <ArrowRightIcon />
          </DiscoverLink>
        </Flex>
      </Card>
    </CardLink>
  );
};
